///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { PlannerFeatureCollectionDTO, ProjectDTO } from '../../../../generated/shared-types.model';
import { _cloneDeep } from '../../../../shared/utils';
import { GtthLogicalTopology } from '../../../../project-view-panel/logical-topology/entities/logical-topology';
import { ProjectDialogLayerGeneralPanelComponent } from './layer-general-panel/project-dialog-layer-general-panel.component';
import { SelectProductsComponent } from './select-products/select-products.component';
import { GtthHistoryService } from '../../../../project-view-panel/logical-topology/engine/history-service';
import { GtthProjectStateService } from '../../../../project-view-panel/logical-topology/engine/project-state.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-project-dialog-product-config-tab',
    templateUrl: 'project-dialog-product-config-tab.component.html',
    standalone: false
})
export class ProjectDialogProductConfigTabComponent implements OnInit {
    @ViewChild(ProjectDialogLayerGeneralPanelComponent) layerGeneralPanelComponent: ProjectDialogLayerGeneralPanelComponent;
    @ViewChild(SelectProductsComponent) layerProductsPanelComponent: SelectProductsComponent;

    @Input() viewInReadOnly: boolean;
    @Input() project: ProjectDTO;
    @Input() logicalConfig: PlannerFeatureCollectionDTO;

    public activeLayerTab: number;
    public layerNames: string[];
    public copyFromLayerIndex: number;

    public readonlyProductsConfiguration: boolean;

    public frozenPairs: Map<number, number[]>;

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly projectStateService: GtthProjectStateService
    ) {}

    ngOnInit(): void {
        const projectIsOpened = this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === this.project?.id;
        const newProject = !isDefined(this.project?.id);
        this.readonlyProductsConfiguration = this.viewInReadOnly || (!projectIsOpened && !newProject);

        this.activeLayerTab = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
        this.copyFromLayerIndex = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;

        this.layerNames = this.logicalConfig.crs.properties.layers.map((layer) => `${layer.layerPrefix} - ${layer.layerName}`);

        this.frozenPairs = this.readonlyProductsConfiguration || newProject ? new Map() : this.checkFrozenProductPairs();
    }

    changeLayerTab(tab: string) {
        if (this.layerGeneralPanelComponent.validateAndApply() && this.layerProductsPanelComponent.validateAndApply()) {
            this.activeLayerTab = this.layerNames.indexOf(tab);
        }
    }

    copySettingsFromLayer(copyFromLayerIndex: number) {
        this.logicalConfig.crs.properties.tuRequiredRatePerLayer[this.activeLayerTab] =
            this.logicalConfig.crs.properties.tuRequiredRatePerLayer[copyFromLayerIndex];
        this.logicalConfig.crs.properties.defaultHeightPerLayer[this.activeLayerTab] =
            this.logicalConfig.crs.properties.defaultHeightPerLayer[copyFromLayerIndex];
        this.logicalConfig.crs.properties.defaultTuProductPerLayer[this.activeLayerTab] =
            this.logicalConfig.crs.properties.defaultTuProductPerLayer[copyFromLayerIndex];
        this.logicalConfig.crs.properties.defaultBuProductPerLayer[this.activeLayerTab] =
            this.logicalConfig.crs.properties.defaultBuProductPerLayer[copyFromLayerIndex];
        this.logicalConfig.crs.properties.selectedProductsPerLayer[this.activeLayerTab] = _cloneDeep(
            this.logicalConfig.crs.properties.selectedProductsPerLayer[copyFromLayerIndex]
        );
        this.logicalConfig.crs.properties.isBlockedByPole[this.activeLayerTab] = _cloneDeep(
            this.logicalConfig.crs.properties.isBlockedByPole[copyFromLayerIndex]
        );
        this.logicalConfig.crs.properties.autodesignP2mpPerLayer[this.activeLayerTab].pole_block = _cloneDeep(
            this.logicalConfig.crs.properties.autodesignP2mpPerLayer[copyFromLayerIndex].pole_block
        );

        this.layerGeneralPanelComponent.refreshForm();
        this.layerProductsPanelComponent.refreshForm();
    }

    validateAndApply(): boolean {
        const simplePanelValid =
            !this?.layerProductsPanelComponent?.validateAndApply || this.layerProductsPanelComponent.validateAndApply();

        return this.layerGeneralPanelComponent.validateAndApply() && simplePanelValid;
    }

    private checkFrozenProductPairs(): Map<number, number[]> {
        const pairsMap = new Map<number, number[]>();
        const linksIterator = this.historyService?.logicalTopology.getLinksIterator();

        for (const link of linksIterator) {
            if (link.isUserForced()) {
                if (!pairsMap.has(link.getBuUsedProduct())) {
                    pairsMap.set(link.getBuUsedProduct(), []);
                }

                pairsMap.get(link.getBuUsedProduct()).push(link.getTuUsedProduct());
            }
        }

        return pairsMap;
    }
}
